import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BottomSheet from './BottomSheet';
import MaterialsSection from './MaterialsSection';
import TracksOnFile from '../../../../components/Tracks/TracksOnFile';
import { WARMUPS_SONG_ID, SONG_NOTE_STATUS } from '../../../../services/songNotesModel';
import './StudentDrawer.css';

/** Open coaching items on a board song = notes not yet landed. */
const openNoteCount = (song) =>
	(song.notes || []).filter((n) => n.status !== SONG_NOTE_STATUS.LANDED).length;

/**
 * Student drawer — opened from the topbar's identity block (design pass 2,
 * change 6; it was the ⋯ button until batch B). The Student snapshot, the
 * quick Add-a-task row, Full student profile, Student didn't come, and the
 * link-student flow for unlinked lessons.
 *
 * MATERIALS AND REPERTOIRE AT EVERY WIDTH (pass 3 B1, 2026-09-28). Until this
 * pass the drawer carried them only under 768 (`showLibrary={isPhone}`) on the
 * reasoning that the rail carries them at 768 and up. It does not always: on
 * Warm-ups at 768 to 1439 the conducting strip hides the rail, so Materials
 * was unreachable there (card "Materials is hidden at 768-1439px on
 * Warm-ups"), which is the width-gated-information failure the notes column
 * rule exists to prevent. The drawer is now their home at every width; the
 * rail keeps them too, as a convenience, never as the only door. No width
 * logic here at all, and StudentDrawer.test.js pins that.
 *
 * NO "WRAP UP LESSON" HERE (pass 3 B3, Anthony 2026-09-27: "lets try this
 * too"). Complete has ONE door, the Recap drawer's footer, next to the
 * writing it completes; recapEntryPoints.test.js fails if a second appears.
 *
 * BACK TO THE PORTAL (2026-09-21, Anthony from a zoomed PWA: "I don't see
 * where the mobile dashboard is"). Below 1200 CSS px the lesson covers the
 * whole portal chrome, sidebar and hamburger included, so nothing on the page
 * led back to Today except completing the lesson or the student profile. The
 * last row of this drawer is that exit, at EVERY width (redundant beside the
 * sidebar at 1200+, harmless, one shape), and it leaves the lesson OPEN: the
 * LessonSession stays set, so the return pill in the portal topbar brings the
 * teacher straight back. It is rendered for an unlinked lesson too, above the
 * link-a-student search, for the same reason.
 */
const StudentDrawer = ({
	student,
	studentName,
	subLine,
	students = [],
	board = null,
	onLinkStudent,
	onAddTask,
	onMarkNoShow,
	onClose,
	tracksOnFile = [], // pass 3 B2: the student's filed cuts no board song claims
	player = null,
}) => {
	const [search, setSearch] = useState('');
	const [linking, setLinking] = useState(false);
	// Pass 3 A6: "Student didn't come" ends the lesson in one tap and cannot be
	// undone from here, so it asks first, in place, naming the student.
	const [confirmingNoShow, setConfirmingNoShow] = useState(false);
	const [markingNoShow, setMarkingNoShow] = useState(false);
	const noShowName = student?.firstName || (studentName || '').trim().split(/\s+/)[0] || 'this student';

	const confirmNoShow = async () => {
		if (markingNoShow) return;
		setMarkingNoShow(true);
		try {
			await onMarkNoShow();
		} finally {
			setMarkingNoShow(false);
		}
	};

	const repertoire = (board?.songs || []).filter((s) => s.id !== WARMUPS_SONG_ID);

	const matches = search.trim()
		? students
				.filter((s) => {
					const q = search.toLowerCase();
					const name = (s.fullName || `${s.firstName || ''} ${s.lastName || ''}`).toLowerCase();
					return name.includes(q) || s.email?.toLowerCase().includes(q);
				})
				.slice(0, 8)
		: [];

	const link = async (s) => {
		setLinking(true);
		try {
			await onLinkStudent(s);
			onClose();
		} catch (err) {
			console.error('Error linking student:', err);
			setLinking(false);
		}
	};

	return (
		<BottomSheet
			title={studentName || 'Lesson'}
			titleId="ld-drawer-title"
			onClose={onClose}
			className="ld-drawer">
			{student ? (
				<>
					<section className="ld-drawer__section">
						<h3 className="ld-drawer__eyebrow">Student</h3>
						<ul className="ld-drawer__rows">
							{subLine && (
								<li>
									<i className="fa-sharp fa-solid fa-masks-theater" aria-hidden="true" />
									{subLine}
								</li>
							)}
							{student.instrument && (
								<li>
									<i className="fa-sharp fa-solid fa-music" aria-hidden="true" />
									{student.instrument}
								</li>
							)}
						</ul>
					</section>

					<MaterialsSection studentId={student.id} studentName={studentName} />

					{/* Tracks on file (pass 3 B2): also in the Song panel's Tracks
					    mode, but that mode lives on song tabs, and a student whose
					    only filed tracks are for songs not on the board (the case
					    that created this group) may have no song tab at all. So it is
					    a labelled section here too, one tap from any tab, including
					    Warm-ups. Renders nothing when there are none. */}
					{player && tracksOnFile.length > 0 && (
						<section className="ld-drawer__section">
							<TracksOnFile tracks={tracksOnFile} player={player} headingClassName="ld-drawer__eyebrow" />
						</section>
					)}

					{repertoire.length > 0 && (
						<section className="ld-drawer__section">
							<h3 className="ld-drawer__eyebrow">Repertoire</h3>
							<ul className="ld-drawer__rows">
								{repertoire.map((song) => {
									const open = openNoteCount(song);
									return (
										<li key={song.id}>
											<i className="fa-sharp fa-solid fa-music" aria-hidden="true" />
											<span className="ld-drawer__song">{song.name}</span>
											{open > 0 && <span className="ld-drawer__songmeta">{open} open</span>}
										</li>
									);
								})}
							</ul>
						</section>
					)}

					<div className="ld-drawer__links">
						{onAddTask && (
							<button type="button" className="ld-drawer__link" onClick={onAddTask}>
								<i className="fa-sharp fa-solid fa-plus" aria-hidden="true" />
								Add a task
								<i className="fa-sharp fa-solid fa-chevron-right ld-drawer__chev" aria-hidden="true" />
							</button>
						)}
						<Link className="ld-drawer__link" to={`/portal/work/student/${student.id}`}>
							<i className="fa-sharp fa-solid fa-user" aria-hidden="true" />
							Full student profile
							<i className="fa-sharp fa-solid fa-chevron-right ld-drawer__chev" aria-hidden="true" />
						</Link>
						{/* A teacher who opened the dashboard for a student who never
						    arrived had nowhere to say so — Complete implies attended.
						    (Attendance arc, Phase A, card fwMubDlBzniA3JQtbUGi.) */}
						{onMarkNoShow && !confirmingNoShow && (
							<button type="button" className="ld-drawer__link" onClick={() => setConfirmingNoShow(true)}>
								<i className="fa-sharp fa-solid fa-user-xmark" aria-hidden="true" />
								Student didn’t come
								<i className="fa-sharp fa-solid fa-chevron-right ld-drawer__chev" aria-hidden="true" />
							</button>
						)}
						{onMarkNoShow && confirmingNoShow && (
							<div className="ld-drawer__confirm" role="group" aria-label="Confirm no-show">
								<p className="ld-drawer__confirmq">
									Mark {noShowName} as a no-show? The lesson ends and the family is not emailed.
								</p>
								<div className="ld-drawer__confirmbtns">
									<button
										type="button"
										className="ld-drawer__confirmbtn ld-drawer__confirmbtn--danger"
										disabled={markingNoShow}
										onClick={confirmNoShow}>
										Mark no-show
									</button>
									<button
										type="button"
										className="ld-drawer__confirmbtn"
										onClick={() => setConfirmingNoShow(false)}>
										Keep lesson
									</button>
								</div>
							</div>
						)}
						{/* The exit. Leaves the lesson open (see the header comment). */}
						<Link className="ld-drawer__link ld-drawer__link--exit" to="/portal/work/today">
							<i className="fa-sharp fa-solid fa-arrow-left" aria-hidden="true" />
							Back to the portal
							<i className="fa-sharp fa-solid fa-chevron-right ld-drawer__chev" aria-hidden="true" />
						</Link>
					</div>
				</>
			) : (
				<section className="ld-drawer__section">
					<Link className="ld-drawer__link ld-drawer__link--exit" to="/portal/work/today">
						<i className="fa-sharp fa-solid fa-arrow-left" aria-hidden="true" />
						Back to the portal
						<i className="fa-sharp fa-solid fa-chevron-right ld-drawer__chev" aria-hidden="true" />
					</Link>
					<h3 className="ld-drawer__eyebrow">Link a student</h3>
					<p className="ld-drawer__hint">
						This lesson isn't linked yet. Anything you jot is held on the board and
						saves to the student the moment you link one.
					</p>
					<input
						className="ld-drawer__search"
						type="text"
						value={search}
						placeholder="Search students…"
						onChange={(e) => setSearch(e.target.value)}
					/>
					<ul className="ld-drawer__results">
						{matches.map((s) => (
							<li key={s.id}>
								<button type="button" disabled={linking} onClick={() => link(s)}>
									{s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim()}
								</button>
							</li>
						))}
					</ul>
				</section>
			)}
		</BottomSheet>
	);
};

export default StudentDrawer;
